import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatBytes, formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Play, Pause, RotateCw, Ban, Trash2, Folder, Mail, FolderTree, HardDrive, AlertTriangle, RefreshCw, Clock, FileDown, FileText } from "lucide-react";
import { toast } from "sonner";

const LEVEL_COLOR = {
  info: "text-slate-300", success: "text-emerald-400", warning: "text-amber-400", error: "text-red-400",
};

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4" />
        <span className="text-xs uppercase tracking-[0.2em]">{label}</span>
      </div>
      <p className="mt-3 font-heading text-2xl font-bold tracking-tighter">{value}</p>
    </div>
  );
}

export default function MigrationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [folders, setFolders] = useState([]);
  const [logs, setLogs] = useState([]);
  const lastLogId = useRef(0);
  const inFlight = useRef(false);
  const logBox = useRef(null);

  const poll = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const [mig, fld, lg] = await Promise.all([
        api.get(`/migrations/${id}`),
        api.get(`/migrations/${id}/folders`),
        api.get(`/migrations/${id}/logs?after=${lastLogId.current}`),
      ]);
      setM(mig.data);
      setFolders(fld.data);
      if (lg.data.length) {
        setLogs((prev) => {
          const seen = new Set(prev.map((l) => l.id));
          const merged = [...prev, ...lg.data.filter((l) => !seen.has(l.id))];
          lastLogId.current = merged.reduce((mx, l) => Math.max(mx, l.id), lastLogId.current);
          return merged;
        });
      }
    } catch (err) {
      if (err.response?.status === 404) navigate("/");
    } finally {
      inFlight.current = false;
    }
  }, [id, navigate]);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 2000);
    return () => clearInterval(t);
  }, [poll]);

  useEffect(() => {
    if (logBox.current) logBox.current.scrollTop = logBox.current.scrollHeight;
  }, [logs]);

  const action = async (verb) => {
    try {
      await api.post(`/migrations/${id}/${verb}`);
      toast.success(`Migration ${verb}`);
      poll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/migrations/${id}`);
      toast.success("Migration deleted");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const downloadReport = async (fmt) => {
    try {
      const res = await api.get(`/migrations/${id}/report?fmt=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const safe = (m.name || "migration").replace(/[^a-z0-9-_ ]/gi, "_");
      a.download = `${safe}-report.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Report downloaded (${fmt.toUpperCase()})`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (!m) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;

  const finished = m.status === "completed" || m.status === "failed";
  const pct = finished ? 100 : (m.total_emails > 0 ? Math.round((m.migrated_emails / m.total_emails) * 100) : 0);

  return (
    <div className="mx-auto max-w-6xl">
      <button onClick={() => navigate("/")} className="mb-6 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-button">
        <ArrowLeft className="h-4 w-4" /> Back to overview
      </button>

      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl font-bold tracking-tighter">{m.name}</h1>
            <StatusBadge status={m.status} />
          </div>
          <p className="mt-2 font-mono text-sm text-muted-foreground">
            {m.source_email} <span className="text-primary">→</span> {m.dest_email}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {(m.status === "pending" || m.status === "failed") && (
            <Button className="gap-2" onClick={() => action("start")} data-testid="detail-start-button">
              <Play className="h-4 w-4" /> Start
            </Button>
          )}
          {m.status === "scheduled" && (
            <Button className="gap-2" onClick={() => action("start")} data-testid="detail-startnow-button">
              <Play className="h-4 w-4" /> Start now
            </Button>
          )}
          {m.status === "completed" && (
            <Button className="gap-2" onClick={() => action("start")} data-testid="detail-sync-button">
              <RefreshCw className="h-4 w-4" /> Sync now
            </Button>
          )}
          {m.status === "running" && (
            <Button variant="outline" className="gap-2" onClick={() => action("pause")} data-testid="detail-pause-button">
              <Pause className="h-4 w-4" /> Pause
            </Button>
          )}
          {m.status === "paused" && (
            <Button className="gap-2" onClick={() => action("resume")} data-testid="detail-resume-button">
              <RotateCw className="h-4 w-4" /> Resume
            </Button>
          )}
          {(m.status === "running" || m.status === "paused" || m.status === "queued") && (
            <Button variant="outline" className="gap-2" onClick={() => action("cancel")} data-testid="detail-cancel-button">
              <Ban className="h-4 w-4" /> Cancel
            </Button>
          )}
          {(m.status === "completed" || m.status === "failed" || m.status === "paused") && (
            <>
              <Button variant="outline" className="gap-2" onClick={() => downloadReport("pdf")} data-testid="report-pdf-button">
                <FileDown className="h-4 w-4" /> Report (PDF)
              </Button>
              <Button variant="outline" className="gap-2" onClick={() => downloadReport("csv")} data-testid="report-csv-button">
                <FileText className="h-4 w-4" /> CSV
              </Button>
            </>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="icon" data-testid="detail-delete-button"><Trash2 className="h-4 w-4" /></Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete migration?</AlertDialogTitle>
                <AlertDialogDescription>This removes the migration and its logs. This cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={remove} data-testid="confirm-delete-button">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {m.error && (
        <div className="mb-6 flex items-start gap-2 border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="font-mono">{m.error}</span>
        </div>
      )}

      {m.status === "scheduled" && m.scheduled_at && (
        <div className="mb-6 flex items-center gap-2 border border-violet-200 bg-violet-50 p-4 text-sm text-violet-700" data-testid="scheduled-banner">
          <Clock className="h-4 w-4 shrink-0" />
          <span>Scheduled to start at <strong>{new Date(m.scheduled_at.endsWith("Z") ? m.scheduled_at : m.scheduled_at + "Z").toLocaleString()}</strong>. It will run automatically, or press "Start now".</span>
        </div>
      )}

      <div className="border border-border bg-card p-6">
        <div className="mb-2 flex justify-between font-mono text-sm">
          <span className="text-muted-foreground">Overall progress</span>
          <span className="font-bold">{pct}%</span>
        </div>
        <div className="relative h-4 w-full overflow-hidden rounded-full bg-muted">
          <div className={`h-full rounded-full bg-primary transition-all duration-500 ${m.status === "running" ? "progress-active" : ""}`}
               style={{ width: `${pct}%` }} data-testid="overall-progress-bar" />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Metric icon={Mail} label="Emails" value={`${m.migrated_emails} / ${m.total_emails || "?"}`} />
        <Metric icon={FolderTree} label="Folders" value={`${m.migrated_folders} / ${m.total_folders || "?"}`} />
        <Metric icon={HardDrive} label="Data moved" value={formatBytes(m.bytes_transferred)} />
        <Metric icon={AlertTriangle} label="Failed" value={m.failed_emails} />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div className="border border-border bg-card">
          <div className="border-b border-border px-6 py-4">
            <h2 className="flex items-center gap-2 font-semibold tracking-tight"><Folder className="h-4 w-4" /> Folders</h2>
          </div>
          <div className="max-h-96 overflow-y-auto p-4">
            {folders.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">No folder data yet.</p>
            ) : (
              <div className="space-y-3">
                {folders.map((f) => {
                  const fp = f.total > 0 ? Math.round((f.migrated / f.total) * 100) : 0;
                  return (
                    <div key={f.id} data-testid={`folder-row-${f.id}`}>
                      <div className="mb-1 flex justify-between font-mono text-xs">
                        <span className="truncate">{f.name}</span>
                        <span className="text-muted-foreground">{f.migrated}/{f.total}</span>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                        <div className={`h-full rounded-full ${f.status === "done" ? "bg-emerald-500" : "bg-primary"}`} style={{ width: `${fp}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="border border-border bg-card">
          <div className="border-b border-border px-6 py-4">
            <h2 className="font-semibold tracking-tight">Live Log</h2>
          </div>
          <div ref={logBox} data-testid="log-viewer" className="h-96 overflow-y-auto bg-slate-950 p-4 font-mono text-xs leading-relaxed">
            {logs.length === 0 ? (
              <p className="text-slate-500">Waiting for activity…</p>
            ) : logs.map((l) => (
              <div key={l.id} className={LEVEL_COLOR[l.level] || "text-slate-300"}>
                <span className="text-slate-600">{l.ts ? new Date(l.ts.endsWith("Z") ? l.ts : l.ts + "Z").toLocaleTimeString() : ""}</span>{" "}
                {l.message}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
